/* ------------------------------------------------------------------
  File: Badge.jsx
  Author: Engr. Shaffa K. Kokro Jr. - CEO, Accend Holdings Ltd.
  Last Modified: 2026-05-18
------------------------------------------------------------------ */

// Purpose:
//   - Provides a reusable badge component for the Accend Holdings website.
//   - Supports status labels, category labels, content type labels,
//     emphasis labels, size variants, surface variants, and optional icons.
//   - Uses the acc-badge namespace to avoid conflicts with Bootstrap.

/**
 * Badge
 *
 * A reusable badge component for displaying small labels and statuses.
 *
 * @param {React.ReactNode} children
 * The badge text or content.
 *
 * @param {React.ReactNode} icon
 * Optional icon displayed before the label.
 *
 * @param {"default" | "brand" | "accent" | "success" | "warning" | "error" | "info" | "neutral" | "inverse"} [variant="default"]
 * Controls the badge visual style.
 *
 * @param {"sm" | "md"} [size="sm"]
 * Controls the badge size.
 *
 * @param {"span" | "div"} [as="span"]
 * Controls the HTML element used to render the badge.
 *
 * @param {boolean} [pill=true]
 * Controls whether the badge uses a pill radius.
 *
 * @param {string} [className=""]
 * Additional custom class names.
 */

import "./Badge.css";

const Badge = ({
  children,
  icon,
  variant = "default",
  size = "sm",
  as: Component = "span",
  pill = true,
  className = "",
  ...props
}) => {
  const badgeClasses = [
    "acc-badge",
    `acc-badge--${variant}`,
    `acc-badge--${size}`,
    pill ? "acc-badge--pill" : "acc-badge--soft-radius",
    icon ? "acc-badge--with-icon" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Component className={badgeClasses} {...props}>
      {icon && (
        <span className="acc-badge__icon" aria-hidden="true">
          {icon}
        </span>
      )}

      <span className="acc-badge__label">{children}</span>
    </Component>
  );
};

export default Badge;